import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type CheckoutStep = "billing" | "shipping" | "payment";

export interface CheckoutState {
  selectedPaymentMethod: string | null;
  currentStep: CheckoutStep;
  completedSteps: CheckoutStep[];
  isCurrentStepFormValid: boolean;
  useBillingForShipping: boolean;
}

const initialState: CheckoutState = {
  selectedPaymentMethod: null,
  currentStep: "billing",
  completedSteps: [],
  isCurrentStepFormValid: false,
  useBillingForShipping: true,
};

export const checkoutSlice = createSlice({
  name: "checkout",
  initialState,
  reducers: {
    setSelectedPaymentMethod: (state, action: PayloadAction<string | null>) => {
      const newMethod = action.payload;
      if (state.selectedPaymentMethod !== newMethod) {
        state.selectedPaymentMethod = newMethod;
        // When the payment step is active, selecting any method makes it valid.
        if (state.currentStep === "payment") {
          state.isCurrentStepFormValid = !!newMethod;
        }
      }
    },
    setCurrentStep: (state, action: PayloadAction<CheckoutStep>) => {
      if (state.currentStep !== action.payload) {
        // Reset validity when changing steps.
        // Exception: If moving to the payment step and a method is already selected, it's valid.
        if (action.payload === "payment" && !!state.selectedPaymentMethod) {
          state.isCurrentStepFormValid = true;
        } else {
          // Billing/Shipping steps always start as invalid until the form is validated.
          state.isCurrentStepFormValid = false;
        }
      }
      state.currentStep = action.payload;
    },
    completeStep: (state, action: PayloadAction<CheckoutStep>) => {
      if (!state.completedSteps.includes(action.payload)) {
        state.completedSteps.push(action.payload);
      }
    },
    goToNextStep: (state) => {
      const steps: CheckoutStep[] = ["billing", "shipping", "payment"];
      const currentIndex = steps.indexOf(state.currentStep);

      // Ensure current step is marked as complete before proceeding.
      if (!state.completedSteps.includes(state.currentStep)) {
        state.completedSteps.push(state.currentStep);
      }
      // If skipping shipping, mark it as complete too.
      if (state.currentStep === "shipping" && state.useBillingForShipping) {
        if (!state.completedSteps.includes("shipping")) {
          state.completedSteps.push("shipping");
        }
      }

      // Move to the next step if not the last one.
      if (currentIndex < steps.length - 1) {
        const nextStep = steps[currentIndex + 1];
        state.currentStep = nextStep;
        // Set initial validity for the next step.
        if (nextStep === "payment") {
          // The payment step is valid if a method has been selected.
          state.isCurrentStepFormValid = !!state.selectedPaymentMethod;
        } else {
          // Billing/Shipping start as invalid until the form is validated.
          state.isCurrentStepFormValid = false;
        }
      }
    },
    goToPreviousStep: (state) => {
      const steps: CheckoutStep[] = ["billing", "shipping", "payment"];
      const currentIndex = steps.indexOf(state.currentStep);

      if (currentIndex > 0) {
        const previousStep = steps[currentIndex - 1];
        state.currentStep = previousStep;
        // Remove current and subsequent steps from completed list when going back.
        state.completedSteps = state.completedSteps.filter(
          (step) => steps.indexOf(step) < currentIndex
        );
        // Reset validity when going back as the form needs re-validation.
        state.isCurrentStepFormValid = false;
      }
    },
    setIsCurrentStepFormValid: (state, action: PayloadAction<boolean>) => {
      if (state.isCurrentStepFormValid !== action.payload) {
        state.isCurrentStepFormValid = action.payload;
      }
    },
    setUseBillingForShipping: (state, action: PayloadAction<boolean>) => {
      if (state.useBillingForShipping !== action.payload) {
        state.useBillingForShipping = action.payload;
        // If checking the box while on the shipping step, mark the step as valid.
        if (state.currentStep === "shipping" && action.payload) {
          state.isCurrentStepFormValid = true;
        } else if (state.currentStep === "shipping" && !action.payload) {
          // If unchecking, validity depends on the form again (which will be handled by the form's own effect).
          state.isCurrentStepFormValid = false;
        }
      }
    },
    resetCheckout: (state) => {
      // Reset all state properties to their initial values.
      Object.assign(state, initialState);
      console.log("[Slice Reset] Checkout state reset.");
    },
  },
});

export const {
  setSelectedPaymentMethod,
  setCurrentStep,
  completeStep,
  goToNextStep,
  goToPreviousStep,
  setIsCurrentStepFormValid,
  setUseBillingForShipping,
  resetCheckout,
} = checkoutSlice.actions;

export default checkoutSlice.reducer;
